import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteProvider } from "@/contexts/SiteContext";
import { SecurityGate } from "@/components/auth/SecurityGate";
import App from "./App.tsx";
import "./index.css";
import "@/lib/i18n"; // Initialize i18n before app renders
import { SentryErrorBoundary } from "@/lib/sentry";
import { preloadCriticalResources, addResourceHints } from "@/utils/preloadCriticalResources";
import { MobileErrorBoundary } from "@/components/optimization/MobileErrorBoundary";
import { installDynamicManifest } from "@/utils/dynamicManifest";
import { checkManifestUpdate } from "@/utils/manifestUpdateNotifier";
import { autoRegisterMalariaRunner } from "@/lib/bloodSmear/onnxRunner";
import { installPermissionDenialMonitor } from "@/lib/monitoring/permissionDenialMonitor";

// Initialize critical optimizations immediately
preloadCriticalResources();
addResourceHints();
installDynamicManifest();
installPermissionDenialMonitor();

// Register the on-device Plasmodium runner. Prefers the runtime
// `ml_model_registry` row, falls back to VITE_MALARIA_MODEL_* env vars,
// otherwise the NullRunner stays active (AI triage off).
void autoRegisterMalariaRunner();

// Defer update check so it never blocks first paint
if (typeof requestIdleCallback === "function") {
  requestIdleCallback(() => { void checkManifestUpdate(); });
} else {
  setTimeout(() => { void checkManifestUpdate(); }, 3000);
}

// Guard: Unregister service workers in Lovable editor preview/iframe contexts
// This protects the editor from stale caches while keeping PWA fully functional in production
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // Assume iframe if cross-origin security blocks access
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((registrations) => {
    registrations.forEach((r) => r.unregister());
  });
}

// Defer Sentry initialization to after first paint
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => {
    import('@/lib/sentry').then(({ initSentry }) => initSentry());
  });
} else {
  // Fallback for Safari
  setTimeout(() => {
    import('@/lib/sentry').then(({ initSentry }) => initSentry());
  }, 2000);
}

// Production console suppression — runs once outside React lifecycle
// to avoid restore/re-suppress gaps during StrictMode or error recovery
if (import.meta.env.PROD) {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <SiteProvider>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SentryErrorBoundary fallback={<div className="min-h-screen bg-background flex items-center justify-center p-4"><div className="text-center"><h1 className="text-2xl font-semibold mb-2">Something went wrong</h1><p className="text-muted-foreground">Please refresh the page to try again.</p></div></div>}>
              <MobileErrorBoundary>
                <SecurityGate>
                  <App />
                </SecurityGate>
              </MobileErrorBoundary>
            </SentryErrorBoundary>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </SiteProvider>
  </StrictMode>,
);
